import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import {
  DisplayFormattedDate,
  EmptyIndicator,
  MoreOptions,
  Search,
  SortBy,
  User
} from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { AdminGroupUsersAdd, AdminGroupUsersRemove } from '.';

import type { AdminGroupUser } from 'shared-types';
import type { AdminGroupUpdateUsers } from './AdminGroup';

interface Props {
  users: AdminGroupUser[];
  updateUsers: (obj: AdminGroupUpdateUsers) => Promise<void>;
}

const AdminGroupUsers = ({ users, updateUsers }: Props) => {
  const { messages, theme } = useReduxState();

  const directlyAddedUserIds = users
    .filter((user) => !user.isAttributeUser)
    .map((user) => user.id);

  const yesProps = {
    icon: 'check' as const,
    iconStyle: { color: theme.successColor }
  };
  const noProps = {
    icon: 'ban' as const,
    iconStyle: { color: theme.dangerColor }
  };

  return (
    <div>
      <Box
        headerContent={
          <div style={{ alignItems: 'center', display: 'flex', gap: 10 }}>
            <AdminGroupUsersAdd
              addedUserIds={directlyAddedUserIds}
              updateUsers={updateUsers}
            />
            <MoreOptions category="lighter" styleContentButtons={false}>
              <AdminGroupUsersRemove updateUsers={updateUsers} />
            </MoreOptions>
          </div>
        }
        padding={0}
        subHeaderContent={
          <div className="subheader-container">
            <div className="search">
              <Search
                onSearch={updateUsers}
                placeholder={messages.usersSearchUsers}
              />
            </div>

            <div className="more-actions">
              <SortBy
                onChange={updateUsers}
                options={[
                  {
                    text: messages.commonName,
                    value: 'name'
                  },
                  {
                    text: messages.commonAdded,
                    value: 'added'
                  },
                  {
                    text: messages.adminGroupUserAdded,
                    value: 'addedUser'
                  },
                  {
                    text: messages.adminGroupUserAttribute,
                    value: 'attributeUser'
                  }
                ]}
                value="name"
              />
            </div>
          </div>
        }
        title={messages.commonUsers}
        titleTooltip={messages.adminGroupUsersTooltip}
      >
        {users.length ? (
          <BoxTable>
            <BoxTableHeader>
              <BoxTableCell cellBreakpoint={breakpoints.medium}>
                {messages.commonUser}
              </BoxTableCell>
              <BoxTableCell cellBreakpoint={breakpoints.medium}>
                {messages.commonAdded}
              </BoxTableCell>
              <BoxTableCell cellBreakpoint={breakpoints.medium}>
                {messages.adminGroupUserAdded}
              </BoxTableCell>
              <BoxTableCell cellBreakpoint={breakpoints.medium}>
                {messages.adminGroupUserAttribute}
              </BoxTableCell>
              <BoxTableCell cellBreakpoint={breakpoints.medium}></BoxTableCell>
            </BoxTableHeader>

            {users.map((user) => (
              <BoxTableRow key={user.id}>
                <BoxTableCell cellBreakpoint={breakpoints.medium}>
                  <User user={user} />
                </BoxTableCell>
                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  icon={['far', 'calendar-alt']}
                >
                  <DisplayFormattedDate date={user.added} />
                </BoxTableCell>
                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  {...(user.isAddedUser ? yesProps : noProps)}
                >
                  {user.isAddedUser ? messages.commonYes : messages.commonNo}
                </BoxTableCell>
                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  {...(user.isAttributeUser ? yesProps : noProps)}
                >
                  {user.isAttributeUser
                    ? messages.commonYes
                    : messages.commonNo}
                </BoxTableCell>
                <BoxTableCell shrink={true} cellBreakpoint={breakpoints.medium}>
                  {!user.isAddedUser ? null : (
                    <MoreOptions styleContentButtons={false}>
                      <AdminGroupUsersRemove
                        isAttributeUser={user.isAttributeUser}
                        userId={user.id}
                        updateUsers={updateUsers}
                      />
                    </MoreOptions>
                  )}
                </BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        ) : (
          <EmptyIndicator icon="user" title={messages.commonNoUsers} />
        )}
      </Box>

      <style jsx>{`
        .subheader-container {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          width: 100%;
        }
        .search {
          min-width: 250px;
        }
        .more-actions {
          align-items: center;
          display: flex;
          gap: 10px;
        }
        @media (max-width: ${breakpoints.small}px) {
          .subheader-container {
            flex-direction: column;
          }
          .more-actions {
            margin: 5px 0;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminGroupUsers;
